import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  DescriptionList,
  ErrorAlert,
  Field,
  Input,
  None,
  PageHeader,
  formatDateTime,
  useDocumentTitle,
} from "@unizpay/ui";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type Me, ROLE_DESCRIPTION, ROLE_LABEL, api } from "../lib/api";

const MIN_LENGTH = 12;
const MAX_LENGTH = 200;

function ProfileCard({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.name ?? "");
  useEffect(() => setName(me.name ?? ""), [me.name]);
  const save = useMutation({
    mutationFn: () => api.updateMe({ name: name.trim() || null }),
    onSuccess: (updated) => queryClient.setQueryData(["me"], updated),
  });
  const changed = name.trim() !== (me.name ?? "");

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <Card title="Profile" description="Your email and role are set by an owner.">
      <DescriptionList
        items={[
          { label: "Email", value: me.email },
          {
            label: "Role",
            value: (
              <>
                <span className="font-medium">{ROLE_LABEL[me.role]}</span>
                <span className="text-fg-muted"> · {ROLE_DESCRIPTION[me.role]}</span>
              </>
            ),
          },
          { label: "Last sign-in", value: me.last_login_at ? formatDateTime(me.last_login_at) : <None /> },
          { label: "Admin since", value: formatDateTime(me.created_at) },
        ]}
      />
      <form onSubmit={onSubmit} className="mt-5 space-y-3 border-t border-line pt-5">
        <Field label="Name" hint="Shown in the top bar instead of your email.">
          {(p) => (
            <Input
              {...p}
              name="name"
              autoComplete="name"
              maxLength={100}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                save.reset();
              }}
              className="max-w-sm"
            />
          )}
        </Field>
        <ErrorAlert error={save.error} />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!changed} loading={save.isPending}>
            Save name
          </Button>
          {save.isSuccess && !changed && <span className="text-[0.8125rem] text-ok">Saved</span>}
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [tried, setTried] = useState(false);
  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setTried(false);
    },
  });

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== next;
  const serverParam = change.error instanceof ApiError ? change.error.param : undefined;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTried(true);
    if (next.length < MIN_LENGTH || next !== confirm) return;
    change.mutate();
  }

  function edit(set: (v: string) => void) {
    return (e: { target: { value: string } }) => {
      set(e.target.value);
      if (change.isSuccess || change.isError) change.reset();
    };
  }

  return (
    <Card
      title="Password"
      description="Changing it signs you out everywhere else. This browser stays signed in."
    >
      <form onSubmit={onSubmit} className="max-w-sm space-y-4">
        <Field
          label="Current password"
          error={serverParam === "current_password" ? change.error?.message : null}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              name="current-password"
              autoComplete="current-password"
              required
              maxLength={MAX_LENGTH}
              value={current}
              onChange={edit(setCurrent)}
            />
          )}
        </Field>
        <Field
          label="New password"
          hint={`At least ${MIN_LENGTH} characters. A few unrelated words are easy to remember and hard to guess.`}
          error={
            (tried || tooShort) && next.length < MIN_LENGTH
              ? `Use at least ${MIN_LENGTH} characters`
              : serverParam === "new_password"
                ? (change.error?.message ?? null)
                : null
          }
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              name="new-password"
              autoComplete="new-password"
              required
              maxLength={MAX_LENGTH}
              value={next}
              onChange={edit(setNext)}
            />
          )}
        </Field>
        <Field
          label="Repeat new password"
          error={(tried || mismatch) && confirm !== next ? "The passwords don't match" : null}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              required
              maxLength={MAX_LENGTH}
              value={confirm}
              onChange={edit(setConfirm)}
            />
          )}
        </Field>
        {/* Errors that belong to no single field (locked account, network). */}
        {change.isError && !serverParam && <ErrorAlert error={change.error} />}
        {change.isSuccess && (
          <Alert tone="ok" title="Password changed">
            {change.data.other_sessions_signed_out === 0
              ? "You had no other sessions."
              : change.data.other_sessions_signed_out === 1
                ? "1 other session was signed out."
                : `${change.data.other_sessions_signed_out} other sessions were signed out.`}
          </Alert>
        )}
        <Button type="submit" loading={change.isPending}>
          Change password
        </Button>
      </form>
    </Card>
  );
}

export function AccountPage() {
  useDocumentTitle("Account");
  // Loaded by the layout already; this reads it from the cache.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
  if (!me.data) return null;
  return (
    <>
      <PageHeader title="Account" description="Your admin account and password." />
      <div className="space-y-6">
        <ProfileCard me={me.data} />
        <PasswordCard />
      </div>
    </>
  );
}
